import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { getParentFqn } from '@/shared/lib/format/fqn';
import { assetKeys, discoveryKeys, glossaryKeys } from '@/shared/lib/query/queryKeys';

import { updateGlossaryTermApi } from '@/features/glossary/api';
import type { UpdateGlossaryTermRequest } from '@/features/glossary/api';

export const useUpdateGlossaryTerm = (termFqn?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateGlossaryTermRequest) => updateGlossaryTermApi(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.all });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.glossaryTree });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.termsAll() });

      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfoPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.dataAssetsPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.schemaPrefix() });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.lineagePrefix() });

      if (termFqn) {
        queryClient.invalidateQueries({ queryKey: glossaryKeys.termDetailByFqnPrefix() });

        const oldParentFqn = getParentFqn(termFqn, true);
        queryClient.invalidateQueries({ queryKey: glossaryKeys.childTerms(oldParentFqn) });

        const newParentFqn = getParentFqn(data.fullyQualifiedName, true);
        if (oldParentFqn !== newParentFqn) {
          queryClient.invalidateQueries({ queryKey: glossaryKeys.childTerms(newParentFqn) });
        }
      }
      notification.success({
        message: NOTIFICATION_MESSAGES.GLOSSARY_TERM_UPDATE_SUCCESS,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });
};
